import { Heading, Text, pxToRem } from "@ottatech/design";
import { useQuery } from "@apollo/client";
import styled from "styled-components";

import { IDashboardBannerNotification } from "..";
import { formatMostRecentCompanies } from "../../Prompts/types/ProfilePerformance";

import {
  Notification,
  ProfileStatus,
  ProfileCompletionStatusDocument,
} from "schema";
import { Link } from "components/Link";

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
`;

const StyledHeading = styled(Heading)`
  padding-bottom: ${pxToRem(16)};
`;

const StyledText = styled(Text)`
  display: inline-block;
`;

const ProfilePerformanceContent: React.FC = () => {
  const { data: userProfileStatus, loading } = useQuery(
    ProfileCompletionStatusDocument,
    {
      fetchPolicy: "cache-and-network",
    }
  );
  const user = userProfileStatus?.me;

  const profileStatus: ProfileStatus | null =
    user?.__typename === "CurrentCandidate" ||
    (user?.__typename === "CurrentAdmin" && user.profileStatus)
      ? user.profileStatus
      : null;

  if (loading || !profileStatus) {
    return null;
  }

  const companyViews = profileStatus?.mostRecentSourcingViews;
  const numCompanyViews = profileStatus?.numSourcingViews;
  const mostRecentCompanies = formatMostRecentCompanies(companyViews);

  const moreThanThreeCompaniesNotificationContent = (
    <>
      <strong>
        {numCompanyViews} companies looked at your profile recently,
      </strong>{" "}
      including {mostRecentCompanies}.
    </>
  );

  const threeOrLessCompaniesNotificationContent = (
    <strong>{mostRecentCompanies} looked at your profile recently.</strong>
  );

  const notificationContent =
    numCompanyViews > 3
      ? moreThanThreeCompaniesNotificationContent
      : threeOrLessCompaniesNotificationContent;

  return (
    <ContentWrapper>
      <StyledHeading size={1}>How&apos;s your profile doing?</StyledHeading>
      <StyledText>
        {notificationContent} Improve it to get more companies interested in
        you.{" "}
        <StyledLink
          to="/profile"
          underline={true}
          data-testid="link-profile-performance-notification"
          data-analytics-id="link-profile-performance-notification"
        >
          Go to profile →
        </StyledLink>
      </StyledText>
    </ContentWrapper>
  );
};

export const ProfilePerformanceNotification: IDashboardBannerNotification = {
  key: "profile-performance-notification",
  name: Notification.ProfilePerformance,
  Content: ProfilePerformanceContent,
  level: "positive",
  hasIcon: false,
};
